/* eslint-disable import/order */
import React from "react";

import statsEmptyState from "../../assets/images/stats-empty-state.svg";
import SharedEmptyState from "../EmptyState";
import yellowCardIcon from "../../assets/images/icons/yellow-card.svg";
import redCardIcon from "../../assets/images/icons/red-card.svg";
import {House} from "lucide-react"
/* eslint-enable import/order */

const RESULT_COLORS = {
  W: "#4AED88",
  D: "#FFD700",
  L: "#FF7B7B",
  default: "#7F8585",
};
const getResultColor = (r) => RESULT_COLORS[r] || RESULT_COLORS.default;

/**
 * Shared StatisticsTab Component
 * Used by both TeamDashboard (public) and TeamOverview (admin)
 *
 * Props:
 *   stats     - stats object (matches, goals, yellowCards, etc.)
 *   teamName  - displayed in KPI card header
 *   hasData   - if false, shows empty state instead of stats
 */
const StatisticsTab = ({ stats, teamName, hasData = true, isReadOnly }) => {
  if (!hasData) {
    return (
      <SharedEmptyState
        illustration={statsEmptyState}
        illustrationClassName="w-100 h-80 object-contain"
        heading="Bring your dashboard to life"
        description="All you need is a little information: after two matches have been played, detailed infographics will be displayed immediately."
        variant="compact"
        className={isReadOnly ? "pt-10" : "pt-20"}
      />
    );
  }

  return (
    <div className="space-y-6 mb-6">
      <section>
        <h2 className="text-lg font-semibold text-app-text mb-4">Match totals</h2>
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-4 items-stretch">
          <div className="flex flex-col justify-between lg:col-span-3">
            <KPICards stats={stats} teamName={teamName} />
            <AverageGPG stats={stats} />
          </div>
          <div className="lg:col-span-2 h-full">
            <WinLossRecord stats={stats} />
          </div>
        </div>
      </section>
      <LatestMatchForm stats={stats} />
      <KeyStatistics stats={stats} />
    </div>
  );
};

/* ─── KPI Cards ─────────────────────────────────────────────────────────── */

const KPICards = ({ stats, teamName }) => (
  <div className="bg-app-bg border border-app-border rounded-2xl overflow-hidden">
    <div className="bg-app-surface border-b border-app-border px-4 flex items-center py-4">
      <p className="text-xs text-app-text-secondary">{teamName} KPI cards</p>
    </div>
    <div className="grid grid-cols-3 gap-4 px-4 flex pt-4 pb-2">
      <StatCard value={stats.matches} label="Matches" />
      <StatCard value={stats.cleanSheets} label="Clean sheets" />
      <StatCard value={stats.goals} label="Goals" />
    </div>
    <div className="grid grid-cols-3 gap-4 px-4 pb-4 pt-2">
      {/* <StatCard value={stats.assists} label="Assists" /> */}
      <StatCardWithIcon value={stats.yellowCards} label="Yellow Cards" icon={yellowCardIcon} />
      <StatCardWithIcon value={stats.redCards} label="Red Cards" icon={redCardIcon} />
    </div>
  </div>
);

const StatCard = ({ value, label }) => (
  <div>
    <p className="text-2xl font-semibold text-app-text">{value}</p>
    <p className="text-xs text-app-text-muted">{label}</p>
  </div>
);

const StatCardWithIcon = ({ value, label, icon }) => {
  const displayValue =
    typeof value === "number"
      ? Number.isInteger(value)
        ? value
        : value.toFixed(2)
      : value;
  return (
    <div className="items-center gap-2">
      <div className="flex">
        <img src={icon} alt="" className="mr-1" />
        <p className="text-2xl font-semibold text-app-text">{displayValue}</p>
      </div>
      <div>
        <p className="text-xs text-app-text-muted">{label}</p>
      </div>
    </div>
  );
};

/* ─── Win-Loss Record ────────────────────────────────────────────────────── */

const WIN_COLOR  = "#8AE190";
const DRAW_COLOR = "#E7BA19";
const LOSS_COLOR = "#FF7B7B";


const WinLossRecord = ({ stats }) => {
  const { winLossRecord } = stats;
  const r = 45;
  const circumference = 2 * Math.PI * r;
  const gap = 13; // px gap between arc segments (absorbed by rounded caps)

  const winArc  = Math.max(0, (winLossRecord.win.percentage / 100 ) * circumference - gap);
  const drawArc = Math.max(0, (winLossRecord.draw.percentage / 100) * circumference - gap);
  const lossArc = Math.max(0, (winLossRecord.loss.percentage / 100) * circumference - gap);

  const drawOffset = winArc > 0 ? -(winArc + gap) : 0 //offset by win arc
  const lossOffset = (winArc > 0 ? -(winArc + gap) : 0) + (drawArc > 0 ? -(drawArc + gap) : 0) //offset by win and draw arc

  const showBg = winArc == 0 && drawArc == 0 && lossArc == 0
  
  return (
    <div className="h-full border border-app-border rounded-2xl overflow-hidden">
      <div className="bg-app-surface border-b border-app-border px-4 flex items-center py-4">
        <p className="text-xs text-app-text-secondary">Win-loss record</p>
      </div>
      <div className="flex items-center justify-between">
        <div className="relative">
          <svg className="w-full h-full transform -rotate-90" viewBox="0 0 120 120">
            {/* Track */}
            <circle cx="60" cy="60" r={r} fill="none" stroke={showBg && "#192634"} strokeWidth="12" />
            {/* Win */}
            {winArc > 0 && (
              <circle
                cx="60" cy="60" r={r} fill="none"
                stroke={WIN_COLOR} strokeWidth="12"
                strokeDasharray={`${winArc} ${circumference}`}
                strokeLinecap="round"
              />
            )}
            {/* Draw */}
            {drawArc > 0 && (
              <circle
                cx="60" cy="60" r={r} fill="none"
                stroke={DRAW_COLOR} strokeWidth="12"
                strokeDasharray={`${drawArc} ${circumference}`}
                strokeDashoffset={drawOffset}
                strokeLinecap="round"
              />
            )}
            {/* Loss */}
            {lossArc > 0 && (
              <circle
                cx="60" cy="60" r={r} fill="none"
                stroke={LOSS_COLOR} strokeWidth="12"
                strokeDasharray={`${lossArc} ${circumference}`}
                strokeDashoffset={lossOffset}
                strokeLinecap="round"
              />
            )}
          </svg>
          <div className="absolute inset-0 flex flex-col items-center justify-center">
            <span className="text-[30px] font-bold text-app-text">
              {winLossRecord.totalMatches}
            </span>
            <span className="text-[14px] text-app-text-muted">Total matches</span>
          </div>
        </div>
        <div className="flex-1 flex-col px-3 items-center space-y-3 min-w-0">
          <LegendItem color={WIN_COLOR}  label="Win"  count={winLossRecord.win.count}  total={winLossRecord.totalMatches} percentage={winLossRecord.win.percentage} />
          <LegendItem color={DRAW_COLOR} label="Draw" count={winLossRecord.draw.count} total={winLossRecord.totalMatches} percentage={winLossRecord.draw.percentage} />
          <LegendItem color={LOSS_COLOR} label="Loss" count={winLossRecord.loss.count} total={winLossRecord.totalMatches} percentage={winLossRecord.loss.percentage} />
        </div>
      </div>
    </div>
  );
};

const LegendItem = ({ color, label, count, total, percentage }) => (
  <div className="items-center">
    <div className="flex items-center mb-1">
      <div className="w-2 h-2 rounded-full mr-1" style={{ backgroundColor: color }} />
      <p className="text-[13px] text-app-text-muted">{label}</p>
    </div>
    <div className="flex items-center text-[16px] font-medium text-app-text">
      <p className="">{count}/{total}</p>
      <div className="w-1.5 h-1.5 rounded-full mx-1.5 bg-app-border" />
      {percentage && <p className="">{`${percentage?.toFixed(1)}%`}</p>}
    </div>
  </div>
);

/* ─── Average GPG ────────────────────────────────────────────────────────── */

const AverageGPG = ({ stats }) => (
   <div className="bg-app-bg border border-app-border rounded-2xl overflow-hidden">
    <div className="bg-app-surface border-b border-app-border px-4 flex items-center py-4">
      <p className="text-xs text-app-text-secondary">{`Average goals per (GPG)`}</p>
    </div>
    <div className="grid grid-cols-3 gap-4 px-4 flex pt-4 pb-4">
    
      <StatCard value={typeof stats.averageGPG.total === "number"
            ? stats.averageGPG.total.toFixed(0)
            : stats.averageGPG.total} label="Total" />
      <StatCardWithIcon value={stats.averageGPG.home} label="Home" icon={House} />
      <StatCardWithIcon value={stats.averageGPG.away} label="Away" icon={House} />

    </div>
  </div>
);


/* ─── Latest Match Form ──────────────────────────────────────────────────── */

const LatestMatchForm = ({ stats }) => (
  <section>
    <h2 className="text-lg font-semibold text-app-text mb-4">Latest match form</h2>
    <div className=" border border-app-border rounded-2xl overflow-hidden">
      <table className="w-full">
        <thead>
          <tr className="bg-app-surface border-b border-app-border">
            <th className="text-left text-xs text-app-text-secondary font-normal py-4 px-4">Venue</th>
            <th className="text-left text-xs text-app-text-secondary font-normal py-4 px-4">Game result</th>
            <th className="text-left text-xs text-app-text-secondary font-normal py-4 px-4">GPG</th>
            <th className="text-left text-xs text-app-text-secondary font-normal py-4 px-4">GD</th>
          </tr>
        </thead>
        <tbody>
          {stats.latestMatchForm.map((row, idx) => (
            <tr key={idx} className="border-b border-app-border last:border-b-0">
              <td className="py-3 px-4 text-sm text-app-text">{row.venue}</td>
              <td className="py-3 px-4">
                <div className="flex gap-1">
                  {row.results.map((result, i) => (
                    <div
                      key={i}
                      className={`w-4 h-4 rounded-full border border-app-bg`}
                      style={{ backgroundColor: getResultColor(result), marginLeft: i > 0 ? -7 : 0}}
                    />
                  ))}
                </div>
              </td>
              <td className="py-4 px-4 text-sm text-app-text">
                {typeof row.gpg === "number" ? row.gpg.toFixed(2) : row.gpg}
              </td>
              <td className="py-4 px-4 text-sm" style={{ color: getResultColor(row.gd?.startsWith("+") ? "W" : row.gd?.startsWith("-") ? "L" : "default") }}>
                {row.gd}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </section>
);

/* ─── Key Statistics ─────────────────────────────────────────────────────── */

const KeyStatistics = ({ stats }) => (
  <section>
    <h2 className="text-lg font-semibold text-app-text mb-4">Key statistics</h2>
    <div className=" border border-app-border rounded-2xl overflow-hidden">
      <table className="w-full">
        <thead>
          <tr className="bg-app-surface border-b border-app-border">
            <th className="text-left text-xs text-app-text-secondary font-normal py-4 px-4">Game result</th>
            <th className="text-left text-xs text-app-text-secondary font-normal py-4 px-4">Win%</th>
            <th className="text-left text-xs text-app-text-secondary font-normal py-4 px-4">Average GPG</th>
            <th className="text-left text-xs text-app-text-secondary font-normal py-4 px-4">Scored</th>
            <th className="text-left text-xs text-app-text-secondary font-normal py-4 px-4">Conceded</th>
            <th className="text-left text-xs text-app-text-secondary font-normal py-4 px-4">Clean sheets</th>
          </tr>
        </thead>
        <tbody>
          {stats.keyStatistics.map((row, idx) => (
            <tr key={idx} className="border-b border-app-border last:border-b-0">
              <td className="py-4 px-4 text-sm text-app-text">{row.gameResult}</td>
              <td className="py-4 px-4 text-sm text-app-text">{row.wins}</td>
              <td className="py-4 px-4 text-sm text-app-text">
                {typeof row.avgGPG === "number" ? row.avgGPG.toFixed(2) : row.avgGPG}
              </td>
              <td className="py-4 px-4 text-sm text-app-text">
                {typeof row.scored === "number" ? row.scored.toFixed(2) : row.scored}
              </td>
              <td className="py-4 px-4 text-sm text-app-text">
                {typeof row.conceded === "number" ? row.conceded.toFixed(2) : row.conceded}
              </td>
              <td className="py-4 px-4 text-sm text-app-text">{row.cleanSheets}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </section>
);

export default StatisticsTab;
